import {
  Box,
  CloseButton,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { BigBadge } from 'components/BigBadge';
import Text from 'components/Text';
import { Plan_Type_Enum } from 'types/databaseEnums';

import BigDevToolsTab from './tabs/BigDevToolsTab';
import ColorsTab from './tabs/ColorsTab';
import DesignAssetsTab from './tabs/DesignAssetsTab';
import DesignSystemTab from './tabs/DesignSystemTab';
import EnvVarsEditorTab from './tabs/EnvVarsEditorTab';
import ExtractTextTab from './tabs/ExtractTextTab';
import FontsTab from './tabs/FontsTab';
import IconsTab from './tabs/IconsTab';
import PackagesTab from './tabs/PackagesTab';
import PhotosTab from './tabs/PhotosTab';
import YourFilesTab from './tabs/YourFilesTab';
import { IAssetsDrawerConfig, TAssetsDrawerTab } from '../types';
import useSandboxTheme from '../useSandboxTheme';

interface IAssetsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  config: IAssetsDrawerConfig;
}

interface ITabItem {
  id: TAssetsDrawerTab;
  label: string;
  section: 'pro' | 'free' | 'tools';
}

const AssetsPanel = ({ isOpen, onClose, config }: IAssetsPanelProps) => {
  const { isFree } = useCurrentUser();
  const { colors } = useSandboxTheme();

  const getDefaultTab = (): TAssetsDrawerTab => {
    if (config.showDesignTab) return 'design-assets';
    if (config.showYourFilesTab) return 'your-files';
    if (config.showPackagesTab) return 'packages';
    return 'photos';
  };

  const [activeTab, setActiveTab] = useState<TAssetsDrawerTab>(getDefaultTab);

  const tabs: ITabItem[] = [
    // PRO section
    ...(config.showDesignTab
      ? [
          { id: 'design-assets' as const, label: 'Design Assets', section: 'pro' as const },
          { id: 'design-system' as const, label: 'Design System', section: 'pro' as const },
        ]
      : []),
    ...(config.showExtractTextTab
      ? [{ id: 'extract-text' as const, label: 'Extract Text', section: 'pro' as const }]
      : []),
    ...(config.showYourFilesTab
      ? [{ id: 'your-files' as const, label: 'Your Files', section: 'pro' as const }]
      : []),
    // FREE RESOURCES section
    ...(config.showPackagesTab
      ? [{ id: 'packages' as const, label: 'Packages', section: 'free' as const }]
      : []),
    { id: 'photos' as const, label: 'Photos', section: 'free' as const },
    { id: 'icons' as const, label: 'Icons', section: 'free' as const },
    { id: 'fonts' as const, label: 'Fonts', section: 'free' as const },
    { id: 'colors' as const, label: 'Colors', section: 'free' as const },
    // TOOLS section
    ...(config.showToolsTab
      ? [
          { id: 'bigdevtools' as const, label: 'BigDevTools', section: 'tools' as const },
          { id: 'env-variables' as const, label: 'Env Variables', section: 'tools' as const },
        ]
      : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'design-assets':
        return (
          <DesignAssetsTab
            designScreenshotUrl={config.designScreenshotUrl}
            entityType={config.entityType}
            entityScreenName={config.entityScreenName}
            projectCards={config.projectCards}
            isDownloadingFigma={config.isDownloadingFigma}
            onFigmaDownload={config.onFigmaDownload}
            githubLink={config.githubLink}
          />
        );
      case 'design-system':
        return (
          <DesignSystemTab
            designScreenshotUrl={config.designScreenshotUrl}
          />
        );
      case 'extract-text':
        return (
          <ExtractTextTab
            isFreeUser={isFree}
            entityType={config.entityType}
            designScreenshotUrl={config.designScreenshotUrl}
            projectCards={config.projectCards}
          />
        );
      case 'your-files':
        return (
          <YourFilesTab
            isFreeUser={isFree}
            entityType={config.entityType}
            entityId={config.entityId || config.entityScreenName}
          />
        );
      case 'packages':
        return (
          <PackagesTab
            packages={config.editorPackages || []}
            onPackagesChange={config.onPackagesChange}
          />
        );
      case 'photos':
        return <PhotosTab />;
      case 'icons':
        return <IconsTab />;
      case 'fonts':
        return <FontsTab />;
      case 'colors':
        return <ColorsTab />;
      case 'bigdevtools':
        return <BigDevToolsTab />;
      case 'env-variables':
        return (
          <EnvVarsEditorTab
            entityType={config.entityType}
            entityId={config.entityId || config.entityScreenName}
          />
        );
      default:
        return null;
    }
  };

  const renderSectionHeader = (label: string) => (
    <Text
      fontSize="micro"
      fontWeight="semibold"
      color={colors.textMuted}
      textTransform="uppercase"
      letterSpacing="wide"
      px={3}
      py={1.5}
    >
      {label}
    </Text>
  );

  if (!isOpen) return null;

  let lastSection: string | undefined;

  return (
    <Box
      position="absolute"
      bottom="100%"
      left={0}
      width="600px"
      maxW="100%"
      height="500px"
      bg={colors.shellBg}
      borderWidth="1px"
      borderColor={colors.shellBorder}
      borderBottomWidth={0}
      borderLeftWidth={0}
      borderTopRightRadius="lg"
      boxShadow={colors.popoverShadow}
      zIndex={1000}
      overflow="hidden"
    >
      {/* Header */}
      <HStack
        px={4}
        py={2}
        borderBottomWidth="1px"
        borderColor={colors.shellBorder}
        justifyContent="space-between"
      >
        <Text fontSize="small" fontWeight="semibold">
          Assets
        </Text>
        <CloseButton
          size="sm"
          color={colors.textTertiary}
          _hover={{ color: colors.textPrimary, bg: colors.hoverBg }}
          onClick={onClose}
        />
      </HStack>

      {/* Body — sidebar + content */}
      <HStack spacing={0} height="calc(100% - 41px)" alignItems="stretch">
        {/* Left sidebar - Tab navigation */}
        <VStack
          width="140px"
          minW="140px"
          borderRightWidth="1px"
          borderColor={colors.shellBorder}
          py={2}
          spacing={0}
          alignItems="stretch"
          overflowY="auto"
          flexShrink={0}
        >
          {tabs.map((tab) => {
            const sectionLabel = tab.section === 'pro' ? 'BIG' : tab.section === 'tools' ? 'Tools' : 'Free Resources';
            const showSectionHeader = sectionLabel !== lastSection;
            if (showSectionHeader) lastSection = sectionLabel;

            return (
              <Box key={tab.id}>
                {showSectionHeader && renderSectionHeader(sectionLabel)}
                <Box
                  px={3}
                  py={1.5}
                  cursor="pointer"
                  bg={activeTab === tab.id ? colors.selectedBg : 'transparent'}
                  borderLeftWidth="3px"
                  borderLeftColor={activeTab === tab.id ? 'purple.400' : 'transparent'}
                  _hover={{ bg: colors.hoverBg }}
                  transition="all 0.15s"
                  onClick={() => setActiveTab(tab.id)}
                >
                  <HStack spacing={2}>
                    <Text
                      fontSize="micro"
                      fontWeight={activeTab === tab.id ? 'semibold' : 'regular'}
                    >
                      {tab.label}
                    </Text>
                    {isFree && (tab.id === 'extract-text' || tab.id === 'your-files' || tab.id === 'bigdevtools' || tab.id === 'env-variables') && (
                      <BigBadge plan={Plan_Type_Enum.Lifetime} size="small" />
                    )}
                  </HStack>
                </Box>
              </Box>
            );
          })}
        </VStack>

        {/* Right content area */}
        <Box flex={1} p={3} overflowY="auto">
          {renderTabContent()}
        </Box>
      </HStack>
    </Box>
  );
};

export default AssetsPanel;
